import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  BookOpen,
  KeyRound,
  LifeBuoy,
  Plug,
  Users,
  Settings,
  LogOut,
  Loader2,
} from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { agentsApi, ApiError, type Agent } from "@/lib/api";
import { TenantLogin } from "./pages/TenantLogin";
import { TenantTicketsList } from "./pages/TenantTicketsList";
import { TenantTicketNew } from "./pages/TenantTicketNew";
import { TenantTicketDetail } from "./pages/TenantTicketDetail";
import { TenantAgents } from "./pages/TenantAgents";
import { TenantApiKeys } from "./pages/TenantApiKeys";
import { TenantConfig } from "./pages/TenantConfig";

// Stoplight Elements is ~2 MB minified — lazy-load it so the rest of the
// tenant shell stays fast. Anyone on /apidocs pays the import cost; nobody
// else does.
const TenantApiDocs = lazy(() =>
  import("./pages/TenantApiDocs").then((m) => ({ default: m.TenantApiDocs })),
);
import { TenantTeam } from "./pages/TenantTeam";
import { TenantSettings } from "./pages/TenantSettings";

const CS_ROLE_CODE = "cs.ticket_responder";

export function TenantShell({ tenantCode }: { tenantCode: string }) {
  const { user, idToken, loading } = useAuth();
  const code = tenantCode.toLowerCase();

  const agentsQuery = useQuery<Agent[], ApiError>({
    queryKey: ["agents", code, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      const res = await agentsApi.list(code, idToken);
      return res.agents;
    },
    enabled: !!idToken && !!user,
  });

  const csHired = (agentsQuery.data ?? []).some((a) => a.role_code === CS_ROLE_CODE);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-midnight flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-cyan" />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<TenantLogin tenantCode={code} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* /apidocs renders full-bleed — Stoplight ships its own left
          navigation drawer, and the content panel benefits from every
          pixel of horizontal space (curl snippets + response bodies stop
          truncating). The other tenant routes share the standard shell. */}
      <Route
        path="/apidocs"
        element={
          <Suspense
            fallback={
              <div className="min-h-screen w-full flex items-center justify-center bg-brand-midnight text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            }
          >
            <TenantApiDocs tenantCode={code} />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-brand-midnight text-brand-ink flex">
            <Sidebar tenantCode={code} csHired={csHired} />
            <div className="flex-1 flex flex-col">
              <Topbar tenantCode={code} />
              <main className="flex-1 overflow-auto">
                <ShellRoutes tenantCode={code} />
              </main>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

// ShellRoutes lives under the splat parent route. We render it inside the
// shell layout; its inner Routes treat the full URL because the parent
// `path="*"` matches everything.
function ShellRoutes({ tenantCode }: { tenantCode: string }) {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/agents" replace />} />
      <Route path="/login" element={<Navigate to="/agents" replace />} />
      <Route path="/agents" element={<TenantAgents tenantCode={tenantCode} />} />
      <Route path="/api-keys" element={<TenantApiKeys tenantCode={tenantCode} />} />
      <Route path="/api-keys/new" element={<TenantApiKeys tenantCode={tenantCode} />} />
      <Route path="/config" element={<TenantConfig tenantCode={tenantCode} />} />
      <Route path="/support" element={<TenantTicketsList tenantCode={tenantCode} />} />
      <Route path="/support/new" element={<TenantTicketNew tenantCode={tenantCode} />} />
      <Route path="/support/:id" element={<TenantTicketDetail tenantCode={tenantCode} />} />
      <Route path="/team" element={<TenantTeam tenantCode={tenantCode} />} />
      <Route path="/settings" element={<TenantSettings tenantCode={tenantCode} />} />
      <Route path="*" element={<Navigate to="/agents" replace />} />
    </Routes>
  );
}

function Sidebar({ tenantCode, csHired }: { tenantCode: string; csHired: boolean }) {
  const loc = useLocation();
  const link = (to: string, Icon: React.ComponentType<{ className?: string }>, label: string) => {
    const active =
      loc.pathname === to || (to === "/support" && loc.pathname.startsWith("/support"));
    return (
      <Link
        key={to}
        to={to}
        className={
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors " +
          (active
            ? "bg-brand-violet/15 text-brand-ink"
            : "text-muted-foreground hover:bg-muted hover:text-foreground")
        }
      >
        <Icon className="h-4 w-4" />
        {label}
      </Link>
    );
  };

  return (
    <aside className="hidden md:flex w-60 flex-col border-r border-border bg-surface-1">
      <div className="h-16 flex items-center px-4 border-b border-border">
        <Logo size="sm" />
      </div>
      <div className="p-3 text-[11px] uppercase tracking-wide text-muted-foreground font-mono">
        {tenantCode.toUpperCase()}
      </div>
      <nav className="px-3 pb-3 space-y-1">
        {link("/agents", Bot, "Agents")}
        {link("/api-keys", KeyRound, "API Keys")}
        {link("/apidocs", BookOpen, "API Docs")}
        {link("/config", Plug, "Config")}
        {csHired && link("/support", LifeBuoy, "Support")}
        {link("/team", Users, "Team")}
        {link("/settings", Settings, "Settings")}
      </nav>
    </aside>
  );
}

function Topbar({ tenantCode }: { tenantCode: string }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <header className="h-16 border-b border-border bg-surface-1 flex items-center justify-between px-6">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Workspace</span>
        <span className="font-mono text-sm text-brand-cyan">{tenantCode}.virtualdesk.ai</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground truncate max-w-[200px]">{user?.email}</span>
        <Button variant="ghost" size="sm" onClick={handleSignOut}>
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>
    </header>
  );
}
